import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  PlusIcon,
  TrashIcon,
  PlayIcon,
  ClockIcon,
  PauseIcon,
} from "@phosphor-icons/react";

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

const CRON_EXAMPLES = [
  ["Every hour", "0 * * * *"],
  ["Daily 8am UTC", "0 8 * * *"],
  ["Weekdays 9am", "0 9 * * 1-5"],
  ["Every 5 min", "*/5 * * * *"],
];

export default function Schedules() {
  const [jobs, setJobs] = useState([]);
  const [collections, setCollections] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    prompt: "",
    trigger_type: "interval",
    cron_expr: "0 8 * * *",
    interval_seconds: 3600,
    timezone: "UTC",
    active: true,
    collection_ids: [],
  });

  const load = async () => {
    const [j, c] = await Promise.all([
      api.get("/schedules"),
      api.get("/collections"),
    ]);
    setJobs(j.data);
    setCollections(c.data);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.name.trim() || !form.prompt.trim()) {
      toast.error("Name and prompt required");
      return;
    }
    try {
      await api.post("/schedules", form);
      toast.success("Schedule created");
      setCreating(false);
      setForm({
        ...form,
        name: "",
        prompt: "",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const toggleActive = async (j) => {
    await api.patch(`/schedules/${j.id}`, { active: !j.active });
    load();
  };

  const runNow = async (j) => {
    await api.post(`/schedules/${j.id}/run`);
    toast.success("Queued");
    setTimeout(load, 1500);
  };

  const remove = async (j) => {
    if (!confirm(`Delete schedule "${j.name}"?`)) return;
    await api.delete(`/schedules/${j.id}`);
    load();
  };

  return (
    <div className="h-full overflow-y-auto" data-testid="schedules-page">
      <div className="max-w-5xl mx-auto p-8 fade-up">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="font-overline text-[10px] text-zinc-500">
              Recurring Agent Runs
            </div>
            <h1 className="font-heading text-4xl tracking-tighter font-medium">
              Schedules
            </h1>
          </div>
          <Button
            data-testid="new-schedule-button"
            onClick={() => setCreating((c) => !c)}
            className="rounded-sm bg-white text-black hover:bg-zinc-200"
          >
            <PlusIcon size={14} className="mr-1" /> New Schedule
          </Button>
        </div>

        {creating && (
          <div
            data-testid="schedule-form"
            className="border border-zinc-800 bg-zinc-900 rounded-sm p-5 mb-8 space-y-4 fade-up"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="font-overline text-[10px] text-zinc-500">
                  Name
                </Label>
                <Input
                  data-testid="schedule-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Morning briefing"
                  className="mt-1 rounded-sm bg-black border-zinc-800"
                />
              </div>
              <div>
                <Label className="font-overline text-[10px] text-zinc-500">
                  Timezone
                </Label>
                <Input
                  value={form.timezone}
                  onChange={(e) =>
                    setForm({ ...form, timezone: e.target.value })
                  }
                  placeholder="UTC, America/New_York, ..."
                  className="mt-1 rounded-sm bg-black border-zinc-800 font-mono text-xs"
                />
              </div>
            </div>

            <div>
              <Label className="font-overline text-[10px] text-zinc-500">
                Prompt
              </Label>
              <Textarea
                data-testid="schedule-prompt"
                value={form.prompt}
                onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                rows={3}
                placeholder="Summarize my notes from the last 24 hours and email me the bullet list."
                className="mt-1 rounded-sm bg-black border-zinc-800 font-mono text-xs"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="font-overline text-[10px] text-zinc-500">
                  Trigger
                </Label>
                <Select
                  value={form.trigger_type}
                  onValueChange={(v) => setForm({ ...form, trigger_type: v })}
                >
                  <SelectTrigger
                    data-testid="schedule-trigger-type"
                    className="mt-1 rounded-sm bg-black border-zinc-800"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="interval">Interval</SelectItem>
                    <SelectItem value="cron">Cron expression</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.trigger_type === "interval" ? (
                <div>
                  <Label className="font-overline text-[10px] text-zinc-500">
                    Every (seconds, min 30)
                  </Label>
                  <Input
                    data-testid="schedule-interval"
                    type="number"
                    min={30}
                    value={form.interval_seconds}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        interval_seconds: parseInt(e.target.value || "0", 10),
                      })
                    }
                    className="mt-1 rounded-sm bg-black border-zinc-800 font-mono"
                  />
                </div>
              ) : (
                <div>
                  <Label className="font-overline text-[10px] text-zinc-500">
                    Cron expression
                  </Label>
                  <Input
                    data-testid="schedule-cron"
                    value={form.cron_expr}
                    onChange={(e) =>
                      setForm({ ...form, cron_expr: e.target.value })
                    }
                    placeholder="0 8 * * *"
                    className="mt-1 rounded-sm bg-black border-zinc-800 font-mono"
                  />
                  <div className="mt-2 flex flex-wrap gap-1">
                    {CRON_EXAMPLES.map(([label, expr]) => (
                      <button
                        key={expr}
                        onClick={() =>
                          setForm({ ...form, cron_expr: expr })
                        }
                        className="font-mono text-[10px] px-2 py-0.5 rounded-sm border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-white"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {collections.length > 0 && (
              <div>
                <Label className="font-overline text-[10px] text-zinc-500">
                  Knowledge Collections
                </Label>
                <div className="mt-2 flex flex-wrap gap-1">
                  {collections.map((c) => {
                    const on = form.collection_ids.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() =>
                          setForm({
                            ...form,
                            collection_ids: on
                              ? form.collection_ids.filter((x) => x !== c.id)
                              : [...form.collection_ids, c.id],
                          })
                        }
                        className={`px-2 py-1 text-xs rounded-sm border ${
                          on
                            ? "border-yellow-700 bg-yellow-950/30 text-yellow-200"
                            : "border-zinc-800 bg-zinc-950 text-zinc-400"
                        }`}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2 border-t border-zinc-800">
              <Button
                onClick={() => setCreating(false)}
                className="rounded-sm bg-transparent border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                data-testid="create-schedule-submit"
                onClick={create}
                className="rounded-sm bg-white text-black hover:bg-zinc-200"
              >
                Create
              </Button>
            </div>
          </div>
        )}

        {jobs.length === 0 && !creating && (
          <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center text-zinc-500">
            <ClockIcon size={28} className="mx-auto text-zinc-600 mb-3" />
            <div className="text-sm">No schedules yet.</div>
            <div className="text-xs text-zinc-600 font-mono mt-1">
              Tell DoppelGanger to do something on a schedule.
            </div>
          </div>
        )}

        <div className="space-y-3">
          {jobs.map((j) => (
            <div
              key={j.id}
              data-testid={`schedule-row-${j.id}`}
              className="border border-zinc-800 bg-zinc-900 rounded-sm p-4 flex items-start gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-heading tracking-tight text-base truncate">
                    {j.name}
                  </div>
                  <Badge
                    variant="outline"
                    className={`rounded-sm font-mono text-[10px] ${
                      j.active
                        ? "border-emerald-700 bg-emerald-950/30 text-emerald-300"
                        : "border-zinc-700 bg-zinc-950 text-zinc-500"
                    }`}
                  >
                    {j.active ? "active" : "paused"}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="rounded-sm font-mono text-[10px] border-zinc-700 bg-zinc-950 text-zinc-300"
                  >
                    {j.trigger_type === "cron"
                      ? `cron · ${j.cron_expr}`
                      : `every ${j.interval_seconds}s`}
                  </Badge>
                </div>
                <div className="text-xs text-zinc-400 mt-2 line-clamp-2">
                  {j.prompt}
                </div>
                <div className="text-[10px] font-mono text-zinc-600 mt-2 flex gap-4 flex-wrap">
                  <span>last: {fmtDate(j.last_run)}</span>
                  <span>next: {fmtDate(j.next_run)}</span>
                  {j.last_status && <span>· {j.last_status}</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  data-testid={`schedule-toggle-${j.id}`}
                  onClick={() => toggleActive(j)}
                  className="w-8 h-8 flex items-center justify-center rounded-sm border border-zinc-800 hover:bg-zinc-800 text-zinc-300"
                  title={j.active ? "Pause" : "Activate"}
                >
                  {j.active ? <PauseIcon size={14} /> : <PlayIcon size={14} />}
                </button>
                <button
                  data-testid={`schedule-run-${j.id}`}
                  onClick={() => runNow(j)}
                  className="w-8 h-8 flex items-center justify-center rounded-sm border border-zinc-800 hover:bg-zinc-800 text-zinc-300"
                  title="Run now"
                >
                  <PlayIcon size={14} weight="fill" />
                </button>
                <button
                  data-testid={`schedule-delete-${j.id}`}
                  onClick={() => remove(j)}
                  className="w-8 h-8 flex items-center justify-center rounded-sm border border-zinc-800 hover:bg-red-950/40 hover:border-red-800 text-zinc-300 hover:text-red-300"
                >
                  <TrashIcon size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
